import { useContext, useEffect, useState } from 'react'
import { SessionPreviewDto, SessionPreviewUploadKey } from '@/api/MetaTrader/SessionDto'
import { PlatformTypeId } from '@/api/Trading-Accounts/Types/PlatformTypeDto'
import { ServerType } from '@/api/Trading-Accounts/Types/ServerType'
import { FormFieldLayout } from '@/components/Form/FormField'
import { MultiSelect } from '@/components/Select'
import { errorToast, infoToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Button } from '@/components/ui/button'
import { useAppDispatch, useAppSelector } from '@/store'
import { fetchServerAction } from '@/store/trading-accounts/trading-accounts.actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { MetaTraderApiClient, PlatformType } from '@/utils/api/metaTraderApi'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid'
import { Upload } from 'antd'
import axios from 'axios'

export const BrokeragePlatformSessionsPage = () => {
  const [sourceServers, setSourceServers] = useState<ServerType[]>([])
  const setToast = useContext(ToastContext)
  const [fileInfo, setFileInfo] = useState<File>()
  const [sessionsFilePreview, setSessionsFilePreview] = useState<SessionPreviewDto[]>()

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchServerAction(PlatformTypeId.MT5, tradingAccountsApiClient))
  }, [])

  const metaTraderApiClient = useApiClient(MetaTraderApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const platformServers = useAppSelector((state) => state.tradingAccounts.platformServers)

  const getServers = () => platformServers.filter((s) => s?.platformType?.id === 2)
  const handleSelectFile = (file: File) => {
    if (file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      setToast(infoToast('Supported format is only csv.'))
      return false
    }
    setFileInfo(file)
    return false
  }
  const handleUploadFile = async () => {
    try {
      if (!fileInfo) {
        setToast(errorToast('Please select a file.'))
        return
      }

      if (sourceServers.length === 0) {
        setToast(errorToast('Please select at least one server.'))
        return
      }

      const formData = new FormData()

      sourceServers.forEach((server, index) => {
        formData.append(`servers[${index}]`, server.id)
      })
      formData.append('file', fileInfo)

      const preview = await metaTraderApiClient.uploadSessionsPreview(
        PlatformType.metatrader5,
        formData
      )
      setSessionsFilePreview(preview)
      setToast(successToast('File uploaded successfully.'))
    } catch (e: unknown) {
      console.error(e)

      if (axios.isAxiosError(e)) {
        const err = e.response?.data?.message

        setToast(errorToast(err ?? 'Error uploading sessions file.'))
      } else {
        setToast(errorToast('An unexpected error occurred.'))
      }
    }
  }
  const handleConfirmationSubmit = async () => {
    if (fileInfo) {
      try {
        setToast(infoToast('Uploading sessions file ...'))
        const formData = new FormData()
        formData.append('file', fileInfo)
        sourceServers.forEach((server, index) => {
          formData.append(`servers[${index}]`, server.id)
        })

        await metaTraderApiClient.uploadSessionsFile(PlatformType.metatrader5, formData)
        setToast(successToast('Sessions file has been uploaded.'))
      } catch (e: unknown) {
        console.error(e)

        if (axios.isAxiosError(e)) {
          const err = e.response?.data?.message
          setToast(errorToast(err ?? 'Error uploading sessions file.'))
        } else {
          setToast(errorToast('An unexpected error occurred.'))
        }
      }
    }
  }
  return (
    <>
      <FormFieldLayout label='Source Server'>
        <MultiSelect
          placeholder='Select Source Server'
          value={sourceServers.map((s) => s.id.toString())}
          allowClear
          size={'lg'}
          className='w-52'
          onChange={(selectedIds: string[]) => {
            const selectedServers = (platformServers || []).filter((pl) =>
              selectedIds.includes(pl.id.toString())
            )
            setSourceServers(selectedServers)
          }}
          options={getServers().map((s) => ({
            label: s.publicName,
            value: s.id,
            id: s.id,
          }))}
        />
      </FormFieldLayout>

      <div style={{ marginBottom: '15px' }}>
        <Upload beforeUpload={handleSelectFile} showUploadList={false}>
          <Button type='button'>{fileInfo?.name ? fileInfo?.name : <>Select File ...</>}</Button>
        </Upload>
        <Button type='button' style={{ marginLeft: '10px' }} onClick={handleUploadFile}>
          Upload
        </Button>
      </div>
      <Grid data={sessionsFilePreview} editField='inEdit' resizable>
        <Column
          field={SessionPreviewUploadKey.symbol}
          title='Symbol'
          minResizableWidth={150}
          width={150}
        />

        <Column
          field={SessionPreviewUploadKey.mondayTrade}
          title='Monday Trade'
          minResizableWidth={150}
          width={150}
        />
        <Column
          field={SessionPreviewUploadKey.mondayQuote}
          title='Monday Quote'
          minResizableWidth={150}
          width={150}
        />

        <Column
          field={SessionPreviewUploadKey.tuesdayTrade}
          title='Tuesday Trade'
          minResizableWidth={150}
          width={150}
        />
        <Column
          field={SessionPreviewUploadKey.tuesdayQuote}
          title='Tuesday Quote'
          minResizableWidth={150}
          width={150}
        />

        <Column
          field={SessionPreviewUploadKey.wednesdayTrade}
          title='Wednesday Trade'
          minResizableWidth={150}
          width={150}
        />
        <Column
          field={SessionPreviewUploadKey.wednesdayQuote}
          title='Wednesday Quote'
          minResizableWidth={150}
          width={150}
        />

        <Column
          field={SessionPreviewUploadKey.thursdayTrade}
          title='Thursday Trade'
          minResizableWidth={150}
          width={150}
        />
        <Column
          field={SessionPreviewUploadKey.thursdayQuote}
          title='Thursday Quote'
          minResizableWidth={150}
          width={150}
        />

        <Column
          field={SessionPreviewUploadKey.fridayTrade}
          title='Friday Trade'
          minResizableWidth={150}
          width={150}
        />
        <Column
          field={SessionPreviewUploadKey.fridayQuote}
          title='Friday Quote'
          minResizableWidth={150}
          width={150}
        />

        <Column
          field={SessionPreviewUploadKey.saturdayTrade}
          title='Saturday Trade'
          minResizableWidth={150}
          width={150}
        />
        <Column
          field={SessionPreviewUploadKey.saturdayQuote}
          title='Saturday Quote'
          minResizableWidth={150}
          width={150}
        />

        <Column
          field={SessionPreviewUploadKey.sundayTrade}
          title='Sunday Trade'
          minResizableWidth={150}
          width={150}
        />
        <Column
          field={SessionPreviewUploadKey.sundayQuote}
          title='Sunday Quote'
          minResizableWidth={150}
          width={150}
        />
      </Grid>
      <div style={{ paddingTop: 24, paddingBottom: 24 }}>
        <Button
          disabled={!sessionsFilePreview}
          type='button'
          onClick={async () => await handleConfirmationSubmit()}
        >
          Submit
        </Button>
      </div>
    </>
  )
}
